<template>
	<v-dialog max-width="1000" persistent v-model="isEdited">
		<v-card v-if="editedCost">
			<egranty-section-divider :text="isAddingNewCost ?  'Dodaj nowy koszt' : 'Edycja kosztu'"/>
			<v-card-text>
				<v-form ref="form">

					<v-select
						v-if="!isEditedAdministrative"
						v-model="editedCost.actionId"
						:items="projectActions"
						item-value="id"
						:item-text='(x) => x.serialNumber + ". " + x.name'
						label="Nazwa działania"
						aria-label="project action"
						:rules="[required]"
						@change="actionChosen"
					/>
					<v-switch
						v-model="isCostOutsideProject"
						:disabled="waitingForActionToBeChosen"
						aria-label="label for switch if cost is outside project"
						label="Pozycja kosztu nieuwzględniona w budżecie"
						@change="costOutsideChosen"
					/>
					<v-switch
						v-if="isCostOutsideProject"
						v-model="isCostReference"
						:disabled="waitingForActionToBeChosen"
						aria-label="label for switch if cost has reference"
						label="Pozycja już dodana w innych dokumentach"
						@change="isReferenceChosen"
					/>
					<v-progress-circular v-if="isLoadingActionCosts"/>
					<v-select
						v-if="!isEditedAdministrative && !isCostOutsideProject && !waitingForActionToBeChosen"
						:disabled="waitingForActionToBeChosen || isLoadingActionCosts"
						v-model="editedCost.actionCostId"
						:items="projectActionCostsFromProject"
						item-value="id"
						:item-text='(x) => editedCost.projectAction ? (editedCost.projectAction.serialNumber + "." + x.serialNumber + ". " + x.name) : ""'
						label="Nazwa pozycji"
						aria-label="cost name"
						:rules="[required]"
						@change="actionCostChosen"
					></v-select>
					<v-select
						:disabled="waitingForActionToBeChosen"
						v-if="isEditedAdministrative && !isCostOutsideProject"
						v-model="editedCost.actionCostId"
						:items="administrativeCostsFromProject"
						item-value="id"
						:item-text='(x) => x.serialNumber + ". " + x.name'
						label="Nazwa pozycji"
						aria-label="cost name"
						:rules="[required]"
						@change="administrativeCostChosen"
					></v-select>
					<v-select
						:disabled="waitingForActionToBeChosen || isLoadingReferences"
						v-if="isCostOutsideProject && isCostReference"
						v-model="editedCost.referenceSerialNumber"
						:items="possibleReferences"
						item-value="serialNumber"
						:item-text='(x) => x.serialNumber + ". " + x.name'
						label="Nazwa pozycji"
						aria-label="cost name"
						:rules="[required]"
					></v-select>
					<egranty-text-input-field
						:disabled="waitingForActionToBeChosen"
						v-if="isCostOutsideProject && !isCostReference"
						label="Nazwa pozycji"
						aria-label="cost name"
						v-model="editedCost.name"
						:max-length="2048"
					/>

					<egranty-text-input-field
						label="Nazwa kosztu"
						v-model="editedCost.description"
						:max-length="1000"
					/>

					<v-layout style="padding-bottom: 10px">
						<h2 style="padding-top: 10px; padding-right: 20px">Podział kosztu na źródła
							finansowania</h2>
						<v-btn
							@click="addCostDivision"
						>
							dodaj źródło
						</v-btn>
					</v-layout>
					<div v-for="(division, index) in editedCost.costDivisions" :key="index">
						<v-layout>
							<v-flex xs4 style="padding-left: 10px">
								<v-select
									label="Źródło finansowania"
									v-model="division.financialSource"
									:rules="[required]"
									:items="financialSources"
									aria-label="financial sources"
								/>
							</v-flex>
							<v-flex xs4 style="padding-left: 10px">
								<egranty-number-input-field
									label="Wartość kosztu"
									:validation-rules="[required]"
									v-model="division.value"
								/>
							</v-flex>
							<egranty-icon-button
								style="padding-top: 10px"
								v-if="editedCost.costDivisions.length > 1" l
								@click="deleteCostDivision(index)"
								abel="usuń"
								color="lightred"
								icon="delete"/>
						</v-layout>
					</div>
					<v-layout v-if="totalCost" row align-center ml-2 mb-3>
						<h3>Łącznie:</h3> &emsp; {{formatMoney(totalCost)}} PLN
					</v-layout>
				</v-form>

				<egranty-validation-error
					v-for="error in errors"
					:key="error"
					:error="error"
				/>
				<v-layout row>
					<v-btn @click="save">zapisz</v-btn>
					<v-btn @click="close">anuluj</v-btn>
				</v-layout>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
	import {ProjectAction} from "@/api/generated/ProjectAction";
	import EgrantySectionDivider from "../../../../genericComponents/EgrantySectionDivider";
	import EgrantyNumberInputField from "../../../../genericComponents/EgrantyNumberInputField";
	import EgrantyIconButton from "../../../../genericComponents/EgrantyIconButton";
	import {Numbers} from "@/utils/Numbers";
	import EgrantyValidationError from "../../../../genericComponents/EgrantyValidationError";
	import EgrantyTextInputField from "../../../../genericComponents/EgrantyTextInputField";
	import {Invoice} from "@/api/generated/Invoice";

	export default {
		name: "EditInvoiceModal",
		components: {
			EgrantyTextInputField,
			EgrantyValidationError, EgrantyIconButton, EgrantyNumberInputField, EgrantySectionDivider},
		mixins: [Numbers],

		data() {
			return {
				isEdited: false,
				isAddingNewCost: false,
				isEditedAdministrative: false,
				isLoadingActionCosts: false,
				isCostOutsideProject: false,
				editedCost: null,
				isLoadingReferences: false,
				isCostReference: false,
				possibleReferences: [],
				required: v => !!v || 'To pole jest wymagane',

				projectActions: [],
				projectActionCostsFromProject: [],
				administrativeCostsFromProject: [],
				errors: [],

				financialSources: [
					{value: "DOTATION", text: "z dotacji"},
					{value: "OWN_FINANCIAL_CONTRIBUTION", text: "środki finansowe własne"},
					{value: "OWN_HUMAN_CONTRIBUTION", text: "wkład osobowy"},
					{value: "OWN_CONTRIBUTION_IN_KIND", text: "wkład rzeczowy"},
					{value: "MONEY_FROM_RECIPIENTS", text: "świadczenie pieniężne od odbiorców"},
					{value: "OTHER", text: "pozostałe"},
					{value: "OTHER_PUBLIC_SOURCE", text: "środki finansowe z innych źródeł publicznych"},
					{value: "INCOME", text: "przychody"},
					{value: "INTEREST", text: "odsetki"},
				],
			};
		},

		watch: {
      "editedCost.referenceSerialNumber": {
        handler(val) {
          let foundRef = this.possibleReferences.find(ref => ref.serialNumber === val);
          if (foundRef) {
            this.editedCost.name = foundRef.name;
          }
        }
      },

			projectId: {
				immediate: true,
				handler(val) {
					if (val !== undefined) {
						this.loadProjectActions();
						this.loadProjectAdministrativeCosts();
					}
				}
			},

			isCostOutsideProject: {
				handler (val) {
					if (!val) this.isCostReference = false;
				}
			}
		},

		computed: {

			waitingForActionToBeChosen() {
				return !this.isEditedAdministrative
					&& (!this.editedCost || !this.editedCost.actionId);
			},

			totalCost() {
				if (!this.editedCost) {
					return 0;
				}
				let total = 0;
				for (let division of this.editedCost.costDivisions) {
					total += Number.parseFloat(division.value);
				}
				return total;
			}
		},

		methods: {

			isReferenceChosen() {
				//zeby upewnić się, że na backend nie poleci jakaś dziwna wartość actionId
				if (this.isEditedAdministrative) {
					this.editedCost.actionId = '';
				}
				this.editedCost.name = "";

				if (this.isCostReference) {
					this.isLoadingReferences = true;
					Invoice.getPossibleReferences(this, this.projectId, this.invoiceId, this.editedCost.actionId, response => {
						this.possibleReferences = response;
						this.isLoadingReferences = false;
					});
				}
			},

			costOutsideChosen() {
				this.editedCost.name = '';
				this.editedCost.description = '';
				this.editedCost.projectActionCost = null;
				this.editedCost.actionCostId = undefined;
			},

			addCostDivision() {
				this.editedCost.costDivisions.push({financialSource: "", value: 0});
			},

			deleteCostDivision(index) {
				this.editedCost.costDivisions.splice(index, 1);
			},

			emptyCost() {
				return {
					projectAction: null,
					actionCostId: '',
					actionId: '',
					projectActionCost: null,
					costDivisions: [
						{
							financialSource: "DOTATION",
							value: ""
						}
					],
				};
			},

			openAddActionCost() {
				this.isEdited = true;
				this.isAddingNewCost = true;
				this.isEditedAdministrative = false;
				this.editedCost = this.emptyCost();
				this.isCostOutsideProject = this.projectActionCost === null;
			},

			openAddAdministrativeCost() {
				this.isEdited = true;
				this.isAddingNewCost = true;
				this.isEditedAdministrative = true;
				this.editedCost = this.emptyCost();
				this.isCostOutsideProject = this.projectActionCost === null;
			},

			openEditCost(cost) {
				this.isEdited = true;
				this.isAddingNewCost = false;
				this.editedCost = JSON.parse(JSON.stringify(cost));
				this.isEditedAdministrative = cost.projectAction === null;
				this.isCostOutsideProject = cost.projectActionCost === null;
				if (!this.isEditedAdministrative) {
					this.editedCost.actionId = cost.projectAction.id;
					this.loadProjectActionCosts();
				}
				if (!this.isCostOutsideProject) {
					this.editedCost.actionCostId = cost.projectActionCost.id;
					this.editedCost.name = cost.projectActionCost.name;
				}
				this.isCostReference = cost.referenceSerialNumber !== null && cost.referenceSerialNumber !== undefined;
				if (this.isCostReference) {
					this.isReferenceChosen();
				}
			},

			close() {
				this.isEdited = false;
			},

			validate() {
				let valid = this.$refs.form.validate();
				if (!valid) {
					this.errors.push("Proszę uzupełnić wymagane pola");
				} else {
					let showConfirmation = false;
					for (let division of this.editedCost.costDivisions) {
						if (division.value < 0.01) {
							division.value = 0.00;
							showConfirmation = true;
						}
					}
					if (showConfirmation) {
						this.$confirm("Czy na pewno chcesz zapisać koszt o wartości 0,00 PLN?", {
							icon: 'warning',
							title: 'Zapisanie kosztu',
							color: 'primary',
							width: 500
						})
							.then(response => {
								if (response) {
									this.isEdited = false;
									this.$emit('save', this.editedCost);
								}
							});
					} else {
						this.isEdited = false;
						this.$emit('save', this.editedCost);
					}
				}
			},

			resetErrors() {
				this.errors = [];
			},

			save() {
				this.resetErrors();
				this.validate();
			},

			loadProjectAdministrativeCosts() {
				ProjectAction.getAdministrativeCosts(
					this,
					this.projectId,
					response => this.administrativeCostsFromProject = response
				);
			},


			loadProjectActions() {
				ProjectAction.get(
					this,
					this.projectId,
					success => this.projectActions = success
				);
			},

			administrativeCostChosen() {
				this.resetErrors();
				this.editedCost.projectActionCost =
					this.administrativeCostsFromProject.find(x => x.id === this.editedCost.actionCostId);
				this.editedCost.name = this.editedCost.projectActionCost.name;
			},

			actionCostChosen() {
				this.resetErrors();
				this.editedCost.projectActionCost =
					this.projectActionCostsFromProject.find(x => x.id === this.editedCost.actionCostId);
				this.editedCost.name = this.editedCost.projectActionCost.name;
			},

			actionChosen() {
				this.resetErrors();
				this.editedCost.projectAction =
					this.projectActions.find(x => x.id === this.editedCost.actionId);
				this.loadProjectActionCosts();
			},

			loadProjectActionCosts() {
				this.isLoadingActionCosts = true;

				ProjectAction.getCosts(this, this.editedCost.actionId, success => {
					this.projectActionCostsFromProject = success;
					this.isLoadingActionCosts = false;
				}, error => {
					this.$messagesBus.$emit("error", "Nie udało się załadować kosztów dla danego działania", error);
					this.isLoadingActionCosts = false;
				});
			},

		},

		props: {
			invoiceId: {},
			projectId: Number,
		}
	};
</script>

<style scoped>

</style>